const parseDateOnly = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const iso = raw.length <= 10 ? `${raw.slice(0, 10)}T12:00:00` : raw;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

/**
 * Leave balance applies from the latest of:
 * - company service commencement date
 * - leave type effective from date
 * - employee date of joining (DOJ)
 */
export const resolveLeaveBalanceStartDate = ({
  serviceCommencementDate,
  employeeDoj,
  effectiveFromDate,
} = {}) => {
  const candidates = [
    parseDateOnly(serviceCommencementDate),
    parseDateOnly(effectiveFromDate),
    parseDateOnly(employeeDoj),
  ].filter(Boolean);

  if (candidates.length === 0) return null;
  return new Date(Math.max(...candidates.map((date) => date.getTime())));
};

export const getLeaveBalanceYearOptions = ({
  startDate,
  currentYear = new Date().getFullYear(),
} = {}) => {
  if (!startDate) {
    return [currentYear, currentYear - 1, currentYear - 2];
  }
  const minYear = startDate.getFullYear();
  const from = Math.min(minYear, currentYear);
  if (from > currentYear) return [currentYear];
  return Array.from({ length: currentYear - from + 1 }, (_, index) => currentYear - index);
};

export const getFirstEligibleMonthInYear = (selectedYear, startDate) => {
  if (!startDate) return 1;
  const startYear = startDate.getFullYear();
  if (selectedYear < startYear) return null;
  if (selectedYear > startYear) return 1;
  return startDate.getMonth() + 1;
};

export const isMonthBeforeBalanceStart = (selectedYear, month, startDate) => {
  if (!startDate || !month) return false;
  const firstEligible = getFirstEligibleMonthInYear(selectedYear, startDate);
  if (firstEligible == null) return true;
  return month < firstEligible;
};

export const isEmployeeEligibleForLeaveBalanceYear = (
  selectedYear,
  { serviceCommencementDate, employeeDoj, effectiveFromDate } = {}
) => {
  const startDate = resolveLeaveBalanceStartDate({
    serviceCommencementDate,
    employeeDoj,
    effectiveFromDate,
  });
  if (!startDate) return true;
  return startDate.getFullYear() <= Number(selectedYear);
};

export const getCompanyServiceCommencementDate = () => {
  try {
    const raw =
      typeof localStorage !== "undefined" ? localStorage.getItem("companyData") : null;
    if (!raw) return null;
    const company = JSON.parse(raw);
    return company?.service_commences_on ?? company?.serviceCommencesOn ?? null;
  } catch {
    return null;
  }
};

export const getEmployeeDoj = (employee) =>
  employee?.joiningDate ??
  employee?.joinDate ??
  employee?.dateOfJoin ??
  employee?.dateOfJoining ??
  employee?.user?.joiningDate ??
  null;

export const getLeaveTypeEffectiveFromDate = (leaveGroup, leaveTypeId) => {
  const configs = leaveGroup?.leaveTypeConfigurations || {};
  const cfg = configs[leaveTypeId] ?? configs[String(leaveTypeId)];
  return (
    cfg?.basic?.effectiveFromDate ||
    cfg?.effectiveFrom ||
    cfg?.effectiveFromDate ||
    null
  );
};

export const resolveGroupBalanceStartDate = (leaveGroup, leaveTypeIds = []) => {
  const serviceCommencementDate = getCompanyServiceCommencementDate();
  const effectiveDates = (leaveTypeIds.length ? leaveTypeIds : [])
    .map((id) => getLeaveTypeEffectiveFromDate(leaveGroup, id))
    .filter(Boolean);

  let effectiveFromDate = null;
  if (effectiveDates.length > 0) {
    const parsed = effectiveDates.map(parseDateOnly).filter(Boolean);
    if (parsed.length > 0) {
      effectiveFromDate = new Date(Math.max(...parsed.map((date) => date.getTime())));
    }
  }

  return resolveLeaveBalanceStartDate({
    serviceCommencementDate,
    effectiveFromDate,
    employeeDoj: null,
  });
};

/**
 * Year options for opening balance upload / leave balance views.
 * Uses the latest of service commencement, leave type effective date, and employee DOJ
 * (evaluated per employee when employees are provided).
 */
export const getOpeningBalanceYearOptions = ({
  serviceCommencementDate,
  groupEmployees = [],
  selectedLeaveTypes = [],
  leaveGroup = null,
  currentYear = new Date().getFullYear(),
} = {}) => {
  const serviceDate =
    serviceCommencementDate ?? getCompanyServiceCommencementDate();

  let earliestStart = null;

  if (groupEmployees.length > 0 && selectedLeaveTypes.length > 0) {
    groupEmployees.forEach((employee) => {
      selectedLeaveTypes.forEach((leaveType) => {
        const startDate = resolveLeaveBalanceStartDate({
          serviceCommencementDate: serviceDate,
          employeeDoj: getEmployeeDoj(employee),
          effectiveFromDate: leaveType.effectiveFromDate,
        });
        if (
          startDate &&
          (!earliestStart || startDate.getTime() < earliestStart.getTime())
        ) {
          earliestStart = startDate;
        }
      });
    });
  }

  if (!earliestStart && selectedLeaveTypes.length > 0 && leaveGroup) {
    earliestStart = resolveGroupBalanceStartDate(
      leaveGroup,
      selectedLeaveTypes.map((lt) => lt.id)
    );
  }

  if (!earliestStart) {
    earliestStart = resolveLeaveBalanceStartDate({
      serviceCommencementDate: serviceDate,
      employeeDoj: null,
      effectiveFromDate: null,
    });
  }

  return getLeaveBalanceYearOptions({ startDate: earliestStart, currentYear });
};
